import { Recipe } from '../types/recipe';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3003/api';

export const fillingApiService = {
  /**
   * Fetch all fillings from the backend
   */
  fetchFillings: async (): Promise<Recipe[]> => {
    try {
      const response = await fetch(`${API_BASE_URL}/fillings`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to fetch fillings');
      }

      return data.fillings;
    } catch (error) {
      console.error('Error fetching fillings from API:', error);
      throw error;
    }
  },

  /**
   * Fetch ingredient names for a specific filling
   */
  fetchFillingIngredients: async (fillingId: string): Promise<string[]> => {
    try {
      const response = await fetch(`${API_BASE_URL}/fillings/${fillingId}/ingredients`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to fetch filling ingredients');
      }

      return data.ingredients;
    } catch (error) {
      console.error('Error fetching filling ingredients from API:', error);
      throw error;
    }
  },

  /**
   * Fetch filling recipe (ingredients with quantities and component references)
   */
  fetchFillingRecipe: async (fillingId: string): Promise<{
    ingredients: { ingredientName: string; quantity: number; unit: string }[];
    components: { componentId: string; componentName: string }[];
  }> => {
    try {
      const response = await fetch(`${API_BASE_URL}/fillings/${fillingId}/recipe`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to fetch filling recipe');
      }

      return {
        ingredients: data.ingredients,
        components: data.components,
      };
    } catch (error) {
      console.error('Error fetching filling recipe from API:', error);
      throw error;
    }
  },

  /**
   * Create a new filling
   */
  createFilling: async (filling: Recipe): Promise<Recipe> => {
    try {
      const response = await fetch(`${API_BASE_URL}/fillings`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(filling)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to create filling');
      }

      return data.filling;
    } catch (error) {
      console.error('Error creating filling:', error);
      throw error;
    }
  },

  /**
   * Update an existing filling
   */
  updateFilling: async (filling: Recipe): Promise<Recipe> => {
    try {
      const response = await fetch(`${API_BASE_URL}/fillings/${filling.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(filling)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to update filling');
      }

      return data.filling;
    } catch (error) {
      console.error('Error updating filling:', error);
      throw error;
    }
  },

  /**
   * Delete a filling by ID
   */
  deleteFilling: async (id: string): Promise<void> => {
    try {
      const response = await fetch(`${API_BASE_URL}/fillings/${id}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to delete filling');
      }
    } catch (error) {
      console.error('Error deleting filling:', error);
      throw error;
    }
  }
};
